import React from "react";
import { Box, Stack, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { cellStateLabel, modeLabel } from "../helper/repeatabilityText";
import RepeatabilityCellCard from "./RepeatabilityCellCard";
import { rgbString } from "../helper/colorConversion";

const CELL_PX = 40;
const CELL_GAP = 3;

/** Diagonal cross drawn over a cell: grey for the automatic rejection, red
 *  for the by-eye one. */
function Cross({ color }) {
    return (
        <svg
            viewBox="0 0 10 10"
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
        >
            <line x1="1" y1="1" x2="9" y2="9" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
            <line x1="9" y1="1" x2="1" y2="9" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        </svg>
    );
}

/**
 * The marked repeatability grid(s) painted with the measured colour of one
 * capture mode. Patches that failed the automatic test are struck through in
 * grey and cannot be clicked; every other patch toggles its by-eye rejection
 * (red cross) on click. Hovering shows every mode's reading and ΔE00.
 */
export default function RepeatabilityGrid({
    repeatability,
    paintMode,
    onPaintModeChange,
    onToggle,
    /** Read-only grids can still report a click (inspect a sample). */
    onInspect,
    readOnly = false,
}) {
    if (!repeatability) return null;
    const { grid_size: gridSize, num_batches: numBatches, cells, modes, limits } = repeatability;
    // Every batch has its own grid (the last one holds the remainder of the
    // palette); payloads from before the batching carry one grid_size only.
    const batchGrid = (batch) => repeatability.batches?.[batch]?.grid_size ?? gridSize;
    const batchCount = (batch) => repeatability.batches?.[batch]?.count;
    const byBatch = Array.from({ length: numBatches }, () => []);
    cells.forEach((cell) => byBatch[cell.batch]?.push(cell));

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                {modes > 1 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">Painted with the</Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={paintMode}
                            onChange={(_e, value) => value && onPaintModeChange?.(value)}
                            aria-label="Capture mode shown on the grid"
                        >
                            {Array.from({ length: modes }, (_, i) => i + 1).map((mode) => (
                                <ToggleButton key={mode} value={mode} sx={{ textTransform: "none", px: 1.5 }}>
                                    {modeLabel(mode)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Typography variant="body2" color="text.secondary">reading</Typography>
                    </Stack>
                )}
            </Stack>

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="flex-start">
                {byBatch.map((batchCells, batch) => {
                    const [rows, cols] = batchGrid(batch);
                    const count = batchCount(batch) ?? batchCells.length;
                    return (
                    <Box key={batch}>
                        {numBatches > 1 && (
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                {`Batch ${batch + 1}`}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {`${count} patches · ${rows} × ${cols}`}
                                </Typography>
                            </Typography>
                        )}
                        <Box
                            role="grid"
                            aria-label={`Repeatability grid batch ${batch + 1}`}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${cols}, ${CELL_PX}px)`,
                                gridTemplateRows: `repeat(${rows}, ${CELL_PX}px)`,
                                gap: `${CELL_GAP}px`,
                                p: 1,
                                borderRadius: 2,
                                bgcolor: "grey.200",
                                width: "fit-content",
                            }}
                        >
                            {batchCells
                                .slice()
                                .sort((a, b) => a.cell - b.cell)
                                .map((cell) => {
                                    const reading = cell.modes[String(paintMode)] || cell.modes["1"];
                                    const autoRejected = !cell.repeatable;
                                    const clickable = readOnly ? Boolean(onInspect) : !autoRejected;
                                    const activate = () => (readOnly ? onInspect?.(cell.sample_id) : onToggle?.(cell.sample_id));
                                    return (
                                        <Tooltip
                                            key={cell.sample_id}
                                            title={<RepeatabilityCellCard cell={cell} limits={limits} paintMode={paintMode} />}
                                            placement="top"
                                        >
                                            <Box
                                                role="gridcell"
                                                data-testid={`repeat-cell-${cell.batch}-${cell.cell}`}
                                                data-state={autoRejected ? "auto" : cell.manual_rejected ? "manual" : "kept"}
                                                aria-label={`Batch ${cell.batch + 1} cell ${cell.cell + 1}: ${cellStateLabel(cell)}`}
                                                tabIndex={clickable ? 0 : -1}
                                                onClick={clickable ? activate : undefined}
                                                onKeyDown={clickable ? (event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        activate();
                                                    }
                                                } : undefined}
                                                sx={{
                                                    position: "relative",
                                                    width: CELL_PX,
                                                    height: CELL_PX,
                                                    borderRadius: 0.5,
                                                    bgcolor: rgbString(reading.rgb),
                                                    opacity: autoRejected ? 0.35 : 1,
                                                    outline: cell.manual_rejected ? "2px solid #d32f2f" : "1px solid rgba(0,0,0,0.15)",
                                                    outlineOffset: -1,
                                                    cursor: clickable ? "pointer" : "default",
                                                    transition: "transform 0.1s ease",
                                                    "&:hover": clickable ? { transform: "scale(1.08)", zIndex: 1 } : {},
                                                }}
                                            >
                                                {autoRejected && <Cross color="rgba(40,40,40,0.9)" />}
                                                {!autoRejected && cell.manual_rejected && <Cross color="#d32f2f" />}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                        </Box>
                    </Box>
                    );
                })}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="caption" color="text.secondary">Grid:</Typography>
                <LegendSwatch color="rgba(90,90,90,0.9)" label="excluded by ΔE00" />
                <LegendSwatch color="#d32f2f" label="excluded manually" />
                {!readOnly && (
                    <Typography variant="caption" color="text.secondary">click a patch to exclude or restore it</Typography>
                )}
                {readOnly && onInspect && (
                    <Typography variant="caption" color="text.secondary">click a patch to inspect the sample</Typography>
                )}
            </Stack>
        </Stack>
    );
}

function LegendSwatch({ color, label }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ position: "relative", width: 16, height: 16, bgcolor: "grey.300", borderRadius: 0.5 }}>
                <Cross color={color} />
            </Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Stack>
    );
}
